import { ChangeDetectorRef, Component, inject, Input, OnInit, ViewChild } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Observable } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CommonModule, Location } from '@angular/common';
import { CalendarModule } from 'primeng/calendar';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AbstractControl, ValidatorFn } from '@angular/forms';
import { ICourseModule } from '../../../../courseModule/models/courseModule.model';
import { CourseModuleService } from '../../../../courseModule/services/courseModule.service';
import { ILesson } from '../../../../lesson/models/lesson.model';
import { ILessonStatus } from '../../../../lesson/models/lessonStatus.model';
import { LessonService } from '../../../../lesson/services/lesson.service';
import { LanguageService } from '../../../../shared/services/language.services';
import { ToastService } from '../../../../shared/services/toast.service';
import { ICourse } from '../../../models/course.model';
import { CourseService } from '../../../services/course.service';
import { CourseModuleLessonListComponent } from '../courseLessonList/courseLessonList.component';
import { Store } from '@ngrx/store';
import { getCourse } from '../../../store/course.actions';
import { getCourseModulesSelector } from '../../../store/course.selectors';
@Component({
    selector: 'clp-course-lesson-data',
    styleUrls: ['./courseLessonData.component.scss'],
    templateUrl: './courseLessonData.component.html',
    imports: [
        RouterModule,
        TranslateModule,
        CommonModule,
        ReactiveFormsModule,
        CalendarModule,
        CourseModuleLessonListComponent
    ],
    providers: [
        LessonService,
        CourseService,
        CourseModuleService,
        ToastrService,
        BrowserAnimationsModule,
        LanguageService
    ]
})
export class CourseLessonDataComponent implements OnInit {
    private _location = inject(Location);
    private courseModuleService = inject(CourseModuleService);
    private lessonService = inject(LessonService);
    private toastService = inject(ToastService);
    private translate = inject(TranslateService);
    private cdRef = inject(ChangeDetectorRef);
    private store = inject(Store);

    @Input() courseId: string | null = null;

    courses: ICourse[] = [];
    courseModules$!: Observable<ICourseModule[]>;
    lessonForm!: FormGroup;
    lesson$!: Observable<ILesson>;
    edit: boolean = false;
    statuses: { value: ILessonStatus, label: string }[] = [];
    nextOrder$! : Observable<number>;

    @ViewChild(CourseModuleLessonListComponent) courseLessonListComponent!: CourseModuleLessonListComponent;

    ngOnInit(): void {
        this.lessonForm = new FormGroup({
            id: new FormControl(''),
            moduleId: new FormControl('', Validators.required),
            title: new FormControl('', [Validators.required, Validators.maxLength(65)]),
            description: new FormControl(''),
            duration: new FormControl(''),
            order: new FormControl(''),
            status: new FormControl(''),
            videoType: new FormControl(),
            videoLink: new FormControl()
        }, { validators: videoFieldsRequiredValidator() });

        this.statuses = Object.entries(ILessonStatus)
        .filter(([key, value]) => typeof value === 'number')
        .map(([key, value]) => ({ value: value as ILessonStatus, label: `lesson.enum.${key}`}));

        this.nextOrder$ = this.lessonService.getNextOrder();
        this.nextOrder$.subscribe(order => {
            this.lessonForm.patchValue({
                order: order
            });
        })

        this.loadCourseModule();
    }

    loadCourseModule(): void {
      if (this.courseId) {
        this.courseModules$ = this.store.select(getCourseModulesSelector);
      }
    }

    editLesson(formValues: any) {
        if (!this.lessonForm.invalid) {
            const lesson: ILesson = {
                ...formValues,
            };

            let return$: Observable<ILesson>;

            if (lesson.id !== undefined && lesson.id !== null && lesson.id !== '') {
              return$ = this.lessonService.editLesson(lesson);
            } else {
              if (lesson.id === '') {
                lesson.id = undefined; // Reset id if it's an empty string
              }
              return$ = this.lessonService.createLesson(lesson);
            }

            return$.subscribe({
                next: (lesson) => {
                    if (lesson?.id) {
                        const message = this.edit ? 'lesson.update.successMessage' : 'lesson.create.successMessage';
                        this.toastService.show(this.translate.instant(message), '');
                        this.clearForm();
                        if (this.courseId !== undefined && this.courseId !== null) {
                          this.store.dispatch(getCourse(this.courseId));
                        }
                    }
                },
                error: (error) => {
                    const errorMessage = this.edit ? 'lesson.update.errorMessage' : 'lesson.create.errorMessage';
                    this.toastService.error(this.translate.instant(errorMessage), '');
                }
            });
        } else {
            this.lessonForm.markAllAsTouched();
        }
    }

    clearForm() {
        this.lessonForm.reset();
        this.edit = false;
        this.lesson$ = new Observable<ILesson>();
        this.lessonForm.markAsPristine();
        this.lessonForm.markAsUntouched();
    }

    populqteLessonForm(lesson: ILesson) {
        this.lessonForm.patchValue({
            id: lesson.id,
            courseId: this.courseId,
            moduleId: lesson.moduleId,
            title: lesson.title,
            description: lesson.description,
            duration: lesson.duration,
            order: lesson.order,
            videoType: lesson.videoType,
            videoLink: lesson.videoLink,
            status: lesson.status
        });
      }

    backClicked() {
        this._location.back();
    }
}



// Custom validator function to check if video fields are required based on status
export function videoFieldsRequiredValidator(): ValidatorFn {
    return (form: AbstractControl) => {
        const status = form.get('status')?.value;
        const videoLink = form.get('videoLink')?.value;
        const videoType = form.get('videoType')?.value;

        // Assuming 'Published' has a specific value (e.g., 1), you may need to adjust based on your enum or values
        const isPublished = status === ILessonStatus.Published;

        if (isPublished && (!videoLink || !videoType)) {
            return { videoFieldsRequired: true };  // Validation error
        }

        return null;  // No errors
    };
}
